/**
 * orgTree.js
 * Client-side cache + hierarchy helpers on top of the real HRMS endpoint
 * (see orgApi.js). Unlike a "load everyone once" API, this backend only
 * ever hands back one employee's manager chain or one employee's full
 * downward subtree at a time — so instead of building the whole tree once,
 * this file builds an index that grows as OrgChartPage.jsx fetches more of
 * it (re-centring, expanding a card).
 *
 * There's no company-wide "everyone with no manager" or "search everyone"
 * endpoint yet, so those operate only over what's already in the cache —
 * see the TODOs in UnassignedPage.jsx and searchEmployees() below.
 */

/** A fresh, empty index. OrgChartPage.jsx keeps one of these in state and
 * merges fetched data into it via mergeChain / mergeReports. */
export function createOrgIndex() {
  return { byId: new Map(), childrenOf: new Map() };
}

function putPerson(index, person) {
  if (person?.empId == null) return;
  index.byId.set(person.empId, person);
}

function linkChild(index, managerId, childId) {
  if (!index.childrenOf.has(managerId)) index.childrenOf.set(managerId, []);
  const kids = index.childrenOf.get(managerId);
  if (!kids.includes(childId)) kids.push(childId);
}

/**
 * mergeChain(index, chainRecords) — chainRecords is the MeReportingTo
 * response for some empId: [self, manager, manager's manager, ..., top].
 * Registers everyone in it and links each to their manager, so the
 * manager-chain cards and the focus card itself are drawn from the same
 * index as everything else.
 *
 * Cuts the chain at the first repeated empId instead of trusting the
 * backend not to loop (PRD's "a circular manager chain is cut at the
 * repeat" rule) — hasLoop / loopEmpId let OrgChartPage.jsx show the warning
 * badge on the node whose manager pointer closes the cycle.
 *
 * Returns { self, ancestors, hasLoop, loopEmpId }.
 */
export function mergeChain(index, chainRecords) {
  const seen = new Set();
  const clean = [];
  let hasLoop = false;
  for (const p of chainRecords ?? []) {
    if (p?.empId == null) continue;
    if (seen.has(p.empId)) { hasLoop = true; break; }
    seen.add(p.empId);
    clean.push(p);
  }

  clean.forEach((p) => putPerson(index, p));
  for (let i = 0; i < clean.length - 1; i++) {
    linkChild(index, clean[i + 1].empId, clean[i].empId);
  }

  return {
    self: clean[0] ?? null,
    ancestors: clean.slice(1),
    hasLoop,
    loopEmpId: hasLoop ? clean[clean.length - 1]?.empId ?? null : null,
  };
}

/**
 * mergeReports(index, rootId, reports) — the ReportingToMe response for
 * rootId: rootId's *entire* downward subtree, every level, flattened into
 * one array — not just direct reports. Each record carries its own real
 * managerId (rootId for direct reports, another record's empId for deeper
 * levels), so children are grouped by that field rather than assumed to
 * all belong to rootId directly — otherwise grand-reports would render as
 * if they reported straight to rootId instead of nesting under their real
 * manager.
 *
 * Also sets childrenOf for every intermediate manager found in the batch
 * (not just rootId), so expanding one of them later is a cache hit instead
 * of a redundant re-fetch of data we already have.
 */
export function mergeReports(index, rootId, reports) {
  const childrenByManager = new Map();
  for (const p of reports ?? []) {
    if (p?.empId == null) continue;
    putPerson(index, p);
    if (!childrenByManager.has(p.managerId)) childrenByManager.set(p.managerId, []);
    childrenByManager.get(p.managerId).push(p.empId);
  }
  index.childrenOf.set(rootId, childrenByManager.get(rootId) ?? []);
  for (const [mgrId, ids] of childrenByManager) {
    if (mgrId !== rootId) index.childrenOf.set(mgrId, ids);
  }
}

/**
 * getVisibleChain(index, focusId, upLevels) — client-side windowing over
 * the manager chain already fetched in full by mergeChain (MeReportingTo
 * returns the whole way to the top in one call). upLevels grows by one
 * each time the user taps expand-up; this just slices what's cached.
 *
 * Returns:
 *   chain        — employeeRecord[], topmost (of the revealed portion) first
 *   canExpandUp  — whether tapping expand-up again would reveal someone new
 */
export function getVisibleChain(index, focusId, upLevels) {
  const full = [];
  const seen = new Set([focusId]);
  let cur = index.byId.get(focusId)?.managerId ?? null;
  while (cur != null && index.byId.has(cur)) {
    if (seen.has(cur)) break; // loop cut
    seen.add(cur);
    full.unshift(index.byId.get(cur));
    cur = index.byId.get(cur)?.managerId ?? null;
  }

  const chain = full.slice(Math.max(0, full.length - upLevels));
  const canExpandUp = full.length > chain.length;
  return { chain, canExpandUp };
}

/**
 * searchEmployees(index, query, limit) — case-insensitive substring match
 * against name OR designation. Used by ChartSearch in OrgChartPage.jsx.
 *
 * TODO: this only searches records already merged into `index` (the
 * currently visible chain + expanded reportees), not the whole company —
 * there's no "search everyone" endpoint yet. Swap the data source once
 * one exists; the matching logic here can stay the same.
 */
export function searchEmployees(index, query, limit = 8) {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return Array.from(index.byId.values())
    .filter(
      (e) =>
        (e.name ?? "").toLowerCase().includes(q) ||
        (e.designation ?? "").toLowerCase().includes(q)
    )
    .slice(0, limit);
}

/**
 * getInitials(name) — "Ravi Sharma" → "RS". Used by the Avatar component
 * whenever a person has no photoUrl. Falls back to "?" for an empty name.
 */
export function getInitials(name) {
  return (name ?? "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "?";
}
